import { supabase } from "@/integrations/supabase/client";
import { processImage } from "@/lib/imageProcess";

const BUCKET = "recipe-images";
const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/**
 * Process (resize + compress) and upload an image to the recipe-images bucket.
 * Returns the public URL. `folder` is typically the recipe's share_id, but for
 * not-yet-saved recipes we accept any short string ("draft" by default).
 *
 * Storage RLS only permits authenticated users to upload directly. For
 * anonymous callers (the common case during the initial save flow) we route
 * the upload through a server-side edge function that uses the service role.
 */
export type UploadProgress = {
  /** Bytes sent so far for the current attempt. */
  loaded: number;
  /** Total bytes for the current attempt (0 if unknown). */
  total: number;
  /** 0-1 fraction. Falls back to indeterminate (-1) when total is unknown. */
  fraction: number;
};

export const uploadRecipeImage = async (
  source: File | string,
  folder = "draft",
  onProgress?: (p: UploadProgress) => void,
): Promise<string> => {
  let blob: Blob;
  try {
    blob = await processImage(source);
  } catch (err) {
    throw new UploadError(
      "processing_failed",
      "We couldn't read that photo on this device.",
    );
  }

  const { data: { session } } = await supabase.auth.getSession();

  if (session) {
    // Authenticated: direct upload to storage.
    const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
    // Report a single 100% tick on success — supabase-js doesn't expose
    // upload progress, but the call still completes quickly.
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, blob, {
        contentType: "image/jpeg",
        cacheControl: "31536000",
        upsert: false,
      });
    if (error) throw error;
    onProgress?.({ loaded: blob.size, total: blob.size, fraction: 1 });
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return data.publicUrl;
  }

  // Anonymous: send through the backend function using XMLHttpRequest so we
  // can surface real upload progress (fetch has no upload progress events on
  // the request body). Mobile Safari has been flaky with
  // supabase.functions.invoke(FormData) and an anonymous POST can hang
  // indefinitely on flaky mobile networks. Each attempt has a 30s timeout
  // and a single retry on timeout/network error so the user gets a real
  // failure instead of an infinite spinner.
  const file = new File([blob], "photo.jpg", { type: "image/jpeg" });

  const attempt = async (): Promise<string> => {
    // FormData must be rebuilt per attempt — some runtimes consume the body.
    const form = new FormData();
    form.append("file", file);
    form.append("folder", folder);

    return await new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${FUNCTIONS_URL}/upload-recipe-image`);
      xhr.setRequestHeader("apikey", PUBLISHABLE_KEY);
      xhr.setRequestHeader("Authorization", `Bearer ${PUBLISHABLE_KEY}`);
      xhr.timeout = 30000;
      xhr.responseType = "text";

      xhr.upload.onprogress = (e) => {
        if (!onProgress) return;
        const total = e.lengthComputable ? e.total : 0;
        const fraction = total > 0 ? e.loaded / total : -1;
        onProgress({ loaded: e.loaded, total, fraction });
      };

      xhr.upload.onload = () => {
        // Body fully sent — show indeterminate "processing on server" state
        // until we get the response.
        onProgress?.({ loaded: file.size, total: file.size, fraction: 1 });
      };

      xhr.onload = () => {
        const text = xhr.responseText || "";
        let body: { url?: string; error?: string } = {};
        try {
          body = text ? JSON.parse(text) : {};
        } catch {
          body = {};
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(
            new UploadError(
              "server_error",
              body.error || `Server error (${xhr.status})`,
              xhr.status,
            ),
          );
          return;
        }
        if (!body.url) {
          reject(new UploadError("server_error", "No URL returned"));
          return;
        }
        resolve(body.url);
      };

      xhr.onerror = () => {
        reject(
          new UploadError(
            "network_error",
            "Network connection lost while uploading.",
          ),
        );
      };
      xhr.ontimeout = () => {
        reject(
          new UploadError(
            "timeout",
            "Upload took too long and was cancelled after 30 seconds.",
          ),
        );
      };

      xhr.send(form);
    });
  };

  const isTransient = (err: unknown): boolean =>
    err instanceof UploadError &&
    (err.code === "timeout" || err.code === "network_error");

  try {
    return await attempt();
  } catch (err) {
    if (!isTransient(err)) throw err;
    // Single retry — covers a dropped request on a flaky mobile network.
    return await attempt();
  }
};

/** Discrete failure modes for image upload, surfaced in the UI. */
export type UploadErrorCode =
  | "timeout"
  | "network_error"
  | "server_error"
  | "processing_failed";

export class UploadError extends Error {
  code: UploadErrorCode;
  status?: number;
  constructor(code: UploadErrorCode, message: string, status?: number) {
    super(message);
    this.name = "UploadError";
    this.code = code;
    this.status = status;
  }
}

/** Whether an image_url still needs to be uploaded before we persist it. */
export const isLocalImage = (url: string | undefined | null): boolean =>
  !!url && (url.startsWith("blob:") || url.startsWith("data:"));

export type RehostErrorCode =
  | "fetch_failed"
  | "not_image"
  | "too_large"
  | "upstream_blocked"
  | "bad_request"
  | "blocked_host";

export class RehostError extends Error {
  code: RehostErrorCode;
  constructor(code: RehostErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Server-side fetch + upload of a remote image URL into the recipe-images bucket.
 * Used for web-search picks, which usually fail CORS when processed client-side.
 */
export const rehostRemoteImage = async (
  url: string,
  folder = "draft",
): Promise<string> => {
  const { data, error } = await supabase.functions.invoke("rehost-recipe-image", {
    body: { url, folder },
  });
  if (error) {
    // Try to parse a typed error code from the response context if available.
    const ctx: any = (error as any).context;
    const code: RehostErrorCode = ctx?.code || "fetch_failed";
    throw new RehostError(code, error.message || "Couldn't rehost image");
  }
  if (data?.error) {
    throw new RehostError(data.code || "fetch_failed", data.error);
  }
  if (!data?.url) {
    throw new RehostError("fetch_failed", "No URL returned");
  }
  return data.url as string;
};
